import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, ExternalLink } from "lucide-react";
import { listMyReviews } from "@/lib/reviews/reviews.functions";
import { StarsDisplay } from "@/components/product/Stars";

export const Route = createFileRoute("/_account/account/reviews")({
  head: () => ({
    meta: [
      { title: "My reviews | FIXHEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MyReviewsPage,
});

function MyReviewsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["account", "reviews"],
    queryFn: () => listMyReviews(),
  });

  const statusChip: Record<string, { label: string; cls: string }> = {
    pending: { label: "Pending review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    approved: { label: "Published", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected: { label: "Not published", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    hidden: { label: "Hidden", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">My reviews</h1>
      <p className="mt-1 text-sm text-slate-500">Reviews you've written for products you own.</p>

      {isLoading ? (
        <div className="mt-6 text-sm text-slate-500">Loading…</div>
      ) : !data || data.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <MessageSquare className="mx-auto h-8 w-8 text-slate-300" />
          <h2 className="mt-3 text-lg font-semibold text-slate-900">No reviews yet</h2>
          <p className="mt-1 text-sm text-slate-500">Head over to your orders and share how the parts fit.</p>
          <Link
            to="/account/orders"
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#172554] px-4 py-2 text-sm font-semibold text-white hover:bg-royal"
          >
            View orders
          </Link>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.map((r: any) => {
            const chip = statusChip[r.status] ?? statusChip.pending;
            return (
              <li key={r.id} className="rounded-xl border bg-white p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <StarsDisplay value={r.rating} size={14} />
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chip.cls}`}>
                      {chip.label}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(r.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="text-sm font-bold text-[#172554]">{r.title}</div>
                  <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">{r.body}</p>
                </div>
                {r.status === "rejected" && r.moderation_note ? (
                  <p className="mt-2 rounded-md bg-rose-50 border border-rose-200 p-2 text-xs text-rose-700">
                    <strong>Reviewer note:</strong> {r.moderation_note}
                  </p>
                ) : null}
                {r.product ? (
                  <div className="mt-3 flex items-center justify-between border-t pt-3">
                    <div className="text-xs text-slate-500">Product: <span className="font-semibold text-slate-700">{r.product.title}</span></div>
                    <Link
                      to={"/p/$slug" as any}
                      params={{ slug: r.product.slug } as any}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline"
                    >
                      Open product <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
